import { useSessionContext } from './session-context'
import { useNotifications } from './use-notifications'
import { useAppUpdateStore, isUpdatePromptVisible } from './app-update'

export function isMobile(): boolean {
	const uaData = (navigator as any).userAgentData
	if (uaData && typeof uaData.mobile === "boolean") return uaData.mobile

	const coarse = matchMedia("(pointer: coarse)").matches
	const noHover = matchMedia("(hover: none)").matches
	const touch = navigator.maxTouchPoints > 0

	return (coarse && noHover) || (touch && coarse)
}

export enum OnboardingStep {
	None = 'none',
	ReloadForUpdate = 'reload-for-update',
	InstallPwa = 'install-pwa',
	EnableNotifications = 'enable-notifications',
	NotificationsDenied = 'notifications-denied',
}

export interface OnboardingBannerState {
	isVisible: boolean
	step: OnboardingStep
	syncWithBackend: () => Promise<void>
}

export function useOnboardingBannerState(): OnboardingBannerState {
	const { session } = useSessionContext()
	const { isPwaStandalone, permission, supportsNotifications, isFullyEnabled, syncWithBackend } = useNotifications({
		userId: session?.userId
	})
	const updatePromptVisible = useAppUpdateStore(isUpdatePromptVisible)

	// Priority 0, also when logged out: it cannot be dismissed, and it hides the steps below until the
	// user reloads, which only takes a moment (those steps can stay unresolved indefinitely)
	if (updatePromptVisible) {
		return {
			isVisible: true,
			step: OnboardingStep.ReloadForUpdate,
			syncWithBackend
		}
	}

	// Don't show banner if user is not logged in
	if (!session?.userId) {
		return {
			isVisible: false,
			step: OnboardingStep.None,
			syncWithBackend
		}
	}

	// Priority 1: Mobile/tablet not installed as PWA
	if (isMobile() && !isPwaStandalone) {
		return {
			isVisible: true,
			step: OnboardingStep.InstallPwa,
			syncWithBackend
		}
	}

	// Priority 2: Notifications not fully enabled
	if (supportsNotifications && !isFullyEnabled) {
		// If permission was denied, need manual browser settings instructions
		if (permission === 'denied') {
			return {
				isVisible: true,
				step: OnboardingStep.NotificationsDenied,
				syncWithBackend
			}
		}

		// Otherwise, direct to settings to enable
		return {
			isVisible: true,
			step: OnboardingStep.EnableNotifications,
			syncWithBackend
		}
	}

	// Otherwise: no banner shown
	return {
		isVisible: false,
		step: OnboardingStep.None,
		syncWithBackend
	}
}
